import React from "react";
import { NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { http } from "@/lib/api";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { isGlobal } from "@/lib/access";
import { NAV_GROUPS, SUPER_NAV_GROUPS } from "@/config/navigation";
import { t } from "@/i18n";
import { resolveAsset } from "@/components/kit/FileUpload";
import { useBranding, assetUrl } from "@/lib/branding";

export function Sidebar({ mobileOpen = false, onClose }) {
  const { user } = useAuth();
  const branding = useBranding();
  const superGlobal = (user?.permissions || []).includes("*") || isGlobal(user, "settings", "view");
  const groups = superGlobal ? SUPER_NAV_GROUPS : NAV_GROUPS;
  const { data: counters } = useQuery({
    queryKey: ["nav-counters"],
    queryFn: async () => (await http.get("/nav/counters")).data,
    refetchInterval: 30000,
  });
  const visible = (item) => {
    if (superGlobal) return true;
    if (item.superOnly) return false;
    if (item.always) return true;
    if (!hasPerm(user, item.perm)) return false;
    return true;
  };

  const platformName = branding?.brand?.name || t("app.name", "Tracify");
  const platformLogo = assetUrl(branding?.brand?.logo_id);
  const brandName = superGlobal ? platformName : (user?.tenant_name || platformName);
  const brandTag = superGlobal ? (branding?.brand?.tagline || t("app.tagline")) : "Partner Network";
  const brandLetter = (brandName || "A").trim().charAt(0).toUpperCase();
  const brandLogo = superGlobal ? platformLogo : resolveAsset(user?.tenant_logo);

  return (
    <>
      {/* Mobile backdrop */}
      <div onClick={onClose} data-testid="sidebar-backdrop"
        className={cn("fixed inset-0 z-30 bg-black/60 backdrop-blur-sm transition-opacity lg:hidden",
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0")} />
      <aside className={cn(
        "fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-white/[0.08] bg-[#0F0F11] transition-transform duration-200 lg:translate-x-0",
        mobileOpen ? "translate-x-0" : "-translate-x-full")}
        data-testid="app-sidebar">
      {/* Brand */}
      <div className="flex h-16 items-center gap-2.5 px-6">
        <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg bg-white text-black">
          {brandLogo ? <img src={brandLogo} alt="" className="h-full w-full object-contain" data-testid="sidebar-logo" /> : <span className="font-display text-base font-black">{brandLetter}</span>}
        </div>
        <div className="min-w-0 leading-tight">
          <div className="truncate font-display text-sm font-bold tracking-tight text-foreground" data-testid="sidebar-brand">{brandName}</div>
          {superGlobal && <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{brandTag}</div>}
        </div>
      </div>

      <nav className="no-scrollbar flex-1 overflow-y-auto px-3 py-2">
        {groups.map((group) => {
          const items = group.items.filter(visible);
          if (items.length === 0) return null;
          return (
            <div key={group.key} className="mb-4">
              <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                {t(group.key)}
              </div>
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    cn(
                      "group relative mb-0.5 flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                      isActive
                        ? "bg-white/[0.06] text-foreground"
                        : "text-muted-foreground hover:bg-white/[0.03] hover:text-foreground",
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && <span className="absolute left-0 h-5 w-0.5 rounded-r bg-white" />}
                      <item.icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                      <span className="flex-1">{item.label}</span>
                      {item.counter && counters?.[item.counter] > 0 && (
                        <span data-testid={`nav-counter-${item.counter}`}
                          className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white/[0.12] px-1.5 text-[11px] font-semibold text-foreground">
                          {counters[item.counter] > 99 ? "99+" : counters[item.counter]}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
    </aside>
    </>
  );
}
